import {
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import MuiTextField from "../text-field/MuiTextField";
import CustomPagination from "./custom-pagination/PaginationCustom";
import compInfo from "./search-filter.json";
import { useLangContext } from "../../../context/langContext";

interface IColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface ICustomTableProps {
  columns: IColumn[];
  rows: any[];
  hasTopPagination?: boolean;
  hasSearchFilter?: boolean;
  count?: number;
  handleChangePage?: (page: number) => void;
  handleChangeRowsPerPage?: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  page?: number;
  rowsPerPage?: number;
  hasWaiting?: boolean;
  styleSx?: any;
  reloadAction?: () => void;
  removeAllAction?: () => void;
}

const dataMsg = {
  fa: "داده ای جهت نمایش وجود ندارد.",
  en: "There is no data to display.",
};
export default function ModalTable({
  columns,
  rows,
  hasSearchFilter,
  hasTopPagination,
  count,
  handleChangePage,
  handleChangeRowsPerPage,
  page,
  rowsPerPage,
  hasWaiting,
  styleSx,
  reloadAction,
  removeAllAction,
}: ICustomTableProps) {
  // const totalPages = Math.ceil(count? / rowsPerPage);
  const { lang } = useLangContext();
  const infos = compInfo[lang];
  const msg = dataMsg[lang];
  return (
    <Paper
      sx={{
        width: "100%",
        height: "fit-content",
        maxHeight: "300px",
        boxShadow: "none",
        ...styleSx,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="fit-content"
        py="10px"
        px="24px"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
      >
        {hasSearchFilter && (
          <Stack
            position="relative"
            width={{ xs: "100%", md: "40%" }}
            direction="row"
            alignItems="center"
            gap="4px"
            pt={{ xs: "12px", md: "0" }}
          >
            <Stack
              sx={{
                position: "absolute",
                top: { xs: "25px", md: "15px" },
                right: "10px",
                scale: "1.3",
                zIndex: "10",
              }}
            >
              <img src="/images/search.svg" alt="search" />
            </Stack>
            <MuiTextField
              width={{ xs: "100%" }}
              placeholder={infos.search}
              sxStyle={{ px: "30px" }}
            />
          </Stack>
        )}
        <Stack
          width="100%"
          justifyContent={hasSearchFilter ? "space-between" : "end"}
          direction="row"
          alignItems="center"
          mr="2px"
        >
          {hasSearchFilter && (
            <Button
              variant="outlined"
              size="small"
              sx={{
                height: "fit-content",
                width: "fit-content",
                px: "0",
                background: ({ palette }) => palette.gray.bg,
                py: { md: "11px", xs: "7px" },
                border: ".8px solid #00000023",
                mb: "2px",
              }}
            >
              <img src="/images/filter.svg" />
            </Button>
          )}
          {hasTopPagination && rows.length > 0 && (
            <CustomPagination
              reloadAction={reloadAction}
              removeAllAction={removeAllAction}
              handlePage={handleChangePage!}
              handlePageSizeChange={handleChangeRowsPerPage!}
              dataLength={count}
              page={page}
              pageSize={rowsPerPage}
              totalPages={
                rowsPerPage && count && Math.ceil(count / rowsPerPage)
              }
            />
          )}
        </Stack>
      </Stack>
      {hasWaiting && <LinearProgress />}
      <TableContainer sx={{ maxHeight: "200px" }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    py: "8px",
                    backgroundColor: (theme) => theme.palette.gray[50],
                  }}
                >
                  <Typography
                    variant="warningMediumBold"
                    color={(theme) => theme.palette.dark[20]}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 || hasWaiting ? (
              rows.map((row) => {
                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.code}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{ py: "8px" }}
                        >
                          <Typography
                            variant="warningRegular"
                            color={(theme) => theme.palette.dark[40]}
                          >
                            {column.format && typeof value === "number"
                              ? column.format(value)
                              : value}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })
            ) : (
              <StyledTableRow>
                <TableCell sx={{ py: "8px" }} colSpan={columns.length}>
                  <Stack
                    width="100%"
                    height="100px"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography
                      variant="h6DemiBold"
                      color={(theme) => theme.palette.dark[40]}
                    >
                      {msg}
                    </Typography>
                  </Stack>
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
    "&:hover": {
      backgroundColor: theme.palette.gray[300],
    },
  },
  "&:nth-of-type(even)": {
    "&:hover": {
      backgroundColor: theme.palette.gray[300],
    },
  },

  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
